import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ActionButton, Checkbox, DatePicker, DefaultButton, Dialog, DialogFooter, DialogType, Dropdown, Icon, PrimaryButton, Stack, TextField, } from "@fluentui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { isValidEmailSecure } from "../loa/utils/emailValidation";
import { CommunicationsServicesPicker } from "./CommunicationsServicesPicker";
import { hasCommunicationsServiceSelection } from "./communications-services";
import { getFirstValidationErrorMessage } from "./validation-utils";
/** Usable public IPv4 counts offered in the static-IP dropdown. */
const STATIC_IP_COUNT_OPTIONS = [0, 1, 2, 6, 14, 30];
export const SiteInABoxForm = ({ form, onChange, onSubmit, validationHandlers, showSiteDetails = true, showServiceSelection = true, onServiceSectionDone, onValidationError, }) => {
    const { t } = useTranslation();
    const reportValidationErrors = React.useCallback((errorRecord, orderedKeys) => {
        const message = getFirstValidationErrorMessage(errorRecord, orderedKeys);
        if (message) {
            onValidationError?.(message);
        }
    }, [onValidationError]);
    const countryOptions = React.useMemo(() => [
        { key: "uk", text: t("United Kingdom") },
        { key: "de", text: t("Germany") },
        { key: "fr", text: t("France") },
        { key: "it", text: t("Italy") },
        { key: "es", text: t("Spain") },
        { key: "nl", text: t("Netherlands") },
        { key: "se", text: t("Sweden") },
        { key: "ch", text: t("Switzerland") },
        { key: "pl", text: t("Poland") },
        { key: "at", text: t("Austria") },
        { key: "ie", text: t("Ireland") },
        { key: "no", text: t("Norway") },
        { key: "dk", text: t("Denmark") },
        { key: "be", text: t("Belgium") },
        { key: "pt", text: t("Portugal") },
        { key: "cz", text: t("Czech Republic") },
        { key: "other", text: t("Other") },
    ], [t]);
    const siteTypeOptions = React.useMemo(() => [
        { key: "parkingYard", text: t("Parking Yard") },
        { key: "vendorFlexSmall", text: t("Vendor Flex Small") },
        { key: "vendorFlexMedium", text: t("Vendor Flex Medium") },
        { key: "vendorFlexLarge", text: t("Vendor Flex Large") },
    ], [t]);
    const staticPublicIpCountOptions = React.useMemo(() => STATIC_IP_COUNT_OPTIONS.map((count) => ({
        key: count,
        text: count === 0 ? t("Not required") : String(count),
    })), [t]);
    const termOptions = React.useMemo(() => [
        { key: "3_months", text: t("3 Months") },
        { key: "6_months", text: t("6 Months") },
        { key: "12_months", text: t("12 Months") },
        { key: "24_months", text: t("24 Months") },
        { key: "36_months", text: t("36 Months") },
    ], [t]);
    const satellitePackagesOptions = React.useMemo(() => [
        { key: "satellite_uk_500gb", text: t("Satellite - UK - 500Gb") },
        { key: "satellite_uk_50gb", text: t("Satellite - UK - 50Gb") },
        { key: "satellite_uk_1tb", text: t("Satellite - UK - 1Tb") },
        { key: "satellite_uk_2tb", text: t("Satellite - UK - 2Tb") },
        {
            key: "satellite_emea_zone1_500gb",
            text: t("Satellite - EMEA - Zone 1 - 500Gb"),
        },
        {
            key: "satellite_emea_zone1_1tb",
            text: t("Satellite - EMEA - Zone 1 - 1Tb"),
        },
        {
            key: "satellite_emea_zone1_1_5tb",
            text: t("Satellite - EMEA - Zone 1 - 1.5Tb"),
        },
        {
            key: "satellite_emea_zone1_2tb",
            text: t("Satellite - EMEA - Zone 1 - 2Tb"),
        },
        {
            key: "satellite_emea_zone1_2_5tb",
            text: t("Satellite - EMEA - Zone 1 - 2.5Tb"),
        },
        {
            key: "satellite_emea_zone1_3tb",
            text: t("Satellite - EMEA - Zone 1 - 3Tb"),
        },
        {
            key: "satellite_emea_zone1_3_5tb",
            text: t("Satellite - EMEA - Zone 1 - 3.5Tb"),
        },
        {
            key: "satellite_emea_zone1_4tb",
            text: t("Satellite - EMEA - Zone 1 - 4Tb"),
        },
        {
            key: "satellite_emea_zone1_4_5tb",
            text: t("Satellite - EMEA - Zone 1 - 4.5Tb"),
        },
        {
            key: "satellite_emea_zone1_5tb",
            text: t("Satellite - EMEA - Zone 1 - 5Tb"),
        },
        {
            key: "satellite_emea_zone1_5_5tb",
            text: t("Satellite - EMEA - Zone 1 - 5.5Tb"),
        },
        {
            key: "satellite_emea_zone1_6tb",
            text: t("Satellite - EMEA - Zone 1 - 6Tb"),
        },
        {
            key: "satellite_emea_zone2_500gb",
            text: t("Satellite - EMEA - Zone 2 - 500Gb"),
        },
        {
            key: "satellite_emea_zone2_1tb",
            text: t("Satellite - EMEA - Zone 2 - 1Tb"),
        },
        {
            key: "satellite_emea_zone2_2tb",
            text: t("Satellite - EMEA - Zone 2 - 2Tb"),
        },
    ], [t]);
    const [errors, setErrors] = React.useState({});
    const [activeSection, setActiveSection] = React.useState(null);
    const [modifiedSections, setModifiedSections] = React.useState(() => new Set());
    const [confirmedSections, setConfirmedSections] = React.useState(() => new Set());
    const SITE_DETAIL_ERROR_KEYS = [
        "customer_name",
        "site_name",
        "entity",
        "site_code",
        "address",
        "reference_id",
        "go_live_date",
        "number_of_sites",
        "contact_email",
    ];
    const SERVICE_ERROR_KEYS = [
        "country",
        "site_type",
        "term_required",
        "satellite_packages",
        "communications_services",
    ];
    const collectSiteDetailErrors = () => {
        const next = {};
        if (!showSiteDetails) {
            return next;
        }
        if (!form.customer_name?.toString().trim()) {
            next.customer_name = t("Customer Name is required");
        }
        if (!form.site_name?.toString().trim()) {
            next.site_name = t("Site Name is required");
        }
        if (!form.entity?.toString().trim()) {
            next.entity = t("Entity is required");
        }
        if (!form.site_code?.toString().trim()) {
            next.site_code = t("Site Code is required");
        }
        if (!form.address?.toString().trim()) {
            next.address = t("Address is required");
        }
        if (!form.reference_id?.toString().trim()) {
            next.reference_id = t("Reference ID is required");
        }
        if (!form.go_live_date) {
            next.go_live_date = t("Go Live Date is required");
        }
        if (!form.number_of_sites || Number(form.number_of_sites) < 1) {
            next.number_of_sites = t("Number of sites must be at least 1");
        }
        if (!form.contact_email?.toString().trim()) {
            next.contact_email = t("Contact Email is required");
        }
        else if (!isValidEmailSecure(form.contact_email)) {
            next.contact_email = t("Please enter a valid contact email address");
        }
        return next;
    };
    const collectServiceSelectionErrors = () => {
        const next = {};
        if (showServiceSelection && !showSiteDetails) {
            if (!form.number_of_sites || Number(form.number_of_sites) < 1) {
                next.number_of_sites = t("Number of sites must be at least 1");
            }
        }
        if (showServiceSelection) {
            if (!form.country) {
                next.country = t("Country is required");
            }
            if (!form.site_type) {
                next.site_type = t("Site type is required");
            }
            if (!form.term_required) {
                next.term_required = t("Term is required");
            }
            const satelliteValid = satellitePackagesOptions.some((option) => option.key === form.satellite_packages);
            if (!form.satellite_packages || !satelliteValid) {
                next.satellite_packages = t("Please select a valid satellite package");
            }
            if (!hasCommunicationsServiceSelection(form)) {
                next.communications_services = t("Add at least one communications service (e.g. Orbit or Galaxy)");
            }
        }
        return next;
    };
    const lastSyncedLineItemIdRef = React.useRef(null);
    /**
     * Cart / "section complete" state is local. When opening a saved row, the form
     * is hydrated from the API but `confirmedSections` starts empty — seed
     * connectivity as complete when the row already passes service validation.
     * Reset section flags when switching to another line item id.
     */
    React.useEffect(() => {
        if (!showServiceSelection) {
            return;
        }
        const rawId = form.id;
        const lineItemId = rawId !== undefined &&
            rawId !== null &&
            String(rawId).trim() !== "" &&
            String(rawId) !== "new"
            ? String(rawId)
            : null;
        if (lineItemId !== lastSyncedLineItemIdRef.current) {
            lastSyncedLineItemIdRef.current = lineItemId;
            setModifiedSections(new Set());
            setConfirmedSections(new Set());
        }
        if (!lineItemId) {
            return;
        }
        const svcErrors = collectServiceSelectionErrors();
        if (Object.keys(svcErrors).length === 0) {
            setConfirmedSections((prev) => {
                if (prev.has("connectivity")) {
                    return prev;
                }
                const next = new Set(prev);
                next.add("connectivity");
                return next;
            });
        }
    }, [showServiceSelection, form]);
    const validateSiteDetails = () => {
        const siteErrors = collectSiteDetailErrors();
        const merged = { ...errors };
        for (const k of SITE_DETAIL_ERROR_KEYS) {
            delete merged[k];
        }
        Object.assign(merged, siteErrors);
        setErrors(merged);
        if (Object.keys(siteErrors).length > 0) {
            reportValidationErrors(siteErrors, SITE_DETAIL_ERROR_KEYS);
        }
        return Object.keys(siteErrors).length === 0;
    };
    const validateServiceSelection = () => {
        const svcErrors = collectServiceSelectionErrors();
        const merged = { ...errors };
        for (const k of SERVICE_ERROR_KEYS) {
            delete merged[k];
        }
        Object.assign(merged, svcErrors);
        setErrors(merged);
        if (Object.keys(svcErrors).length > 0) {
            reportValidationErrors(svcErrors, SERVICE_ERROR_KEYS);
        }
        return Object.keys(svcErrors).length === 0;
    };
    const validateForm = () => {
        const next = {
            ...collectSiteDetailErrors(),
            ...collectServiceSelectionErrors(),
        };
        setErrors(next);
        if (Object.keys(next).length > 0) {
            reportValidationErrors(next, [
                ...SITE_DETAIL_ERROR_KEYS,
                ...SERVICE_ERROR_KEYS,
            ]);
        }
        return Object.keys(next).length === 0;
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSubmit) {
            onSubmit(form);
        }
    };
    React.useEffect(() => {
        if (validationHandlers) {
            validationHandlers({
                validateForm,
                validateSiteDetails,
                validateServiceSelection,
                errors,
            });
        }
    }, [errors, form, validationHandlers]);
    const markActiveSectionAsModified = () => {
        if (!activeSection) {
            return;
        }
        setModifiedSections((prev) => {
            if (prev.has(activeSection)) {
                return prev;
            }
            const next = new Set(prev);
            next.add(activeSection);
            return next;
        });
    };
    const renderNumberField = (name, label, min = 0, max = 999, marginLeft = 0, disabled = false, required = false, fullWidth = false) => (_jsx(TextField, { name: name, label: label, type: "number", value: form[name]?.toString() || "0", onChange: (e, value) => {
            const numValue = value === "" || value === undefined ? 0 : Number(value);
            onChange(e, numValue, name);
        }, min: min, max: max, disabled: disabled, styles: {
            root: {
                width: fullWidth ? "100%" : "48%",
                marginLeft: fullWidth ? 0 : marginLeft,
                boxSizing: "border-box",
            },
        }, required: required, errorMessage: errors[name] }));
    const renderFullWidthNumberField = (name, label, required = false) => renderNumberField(name, label, 0, 999, 0, false, required, true);
    const getBooleanActive = (field) => Boolean(form[field]);
    const getNumberActive = (field) => (Number(form[field]) || 0) > 0;
    const sideButtonStyle = (active) => ({
        root: {
            width: "100%",
            minHeight: 72,
            borderRadius: 12,
            border: `1px solid ${active ? "#605e5c" : "#d1d1d1"}`,
            backgroundColor: active ? "#f3f2f1" : "#ffffff",
            justifyContent: "flex-start",
            padding: "8px 14px",
            fontWeight: 600,
            marginBottom: 12,
        },
        label: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            textAlign: "left",
        },
    });
    const centerTileStyle = (active) => ({
        root: {
            width: "100%",
            minHeight: 72,
            borderRadius: 10,
            border: `1px solid ${active ? "#605e5c" : "#e1dfdd"}`,
            backgroundColor: active ? "#f3f2f1" : "#ffffff",
            padding: "8px 10px",
        },
        label: {
            whiteSpace: "normal",
            lineHeight: 1.2,
            fontSize: 12,
            textAlign: "center",
        },
    });
    const centerTileGridStyle = {
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: 10,
        width: "100%",
    };
    const openSection = (section) => {
        setModifiedSections((prev) => {
            const next = new Set(prev);
            next.delete(section);
            return next;
        });
        setActiveSection(section);
    };
    const closeSection = () => {
        setActiveSection(null);
    };
    /** Service-selection sub-dialogs: validate connectivity fields on Done, not on main Save. */
    const SERVICE_MODAL_SECTIONS = React.useMemo(() => new Set([
        "connectivity",
        "tech-design",
        "deployment",
        "rapid",
        "noc",
        "security",
        "maintenance",
        "lifecycle",
    ]), []);
    const handleSectionDoneClick = async () => {
        if (showServiceSelection &&
            activeSection &&
            SERVICE_MODAL_SECTIONS.has(activeSection) &&
            !validateServiceSelection()) {
            return;
        }
        if (onServiceSectionDone && activeSection) {
            const shouldClose = await onServiceSectionDone(activeSection);
            if (shouldClose === false) {
                return;
            }
        }
        if (activeSection && modifiedSections.has(activeSection)) {
            const sectionToConfirm = activeSection;
            setConfirmedSections((prev) => {
                const next = new Set(prev);
                next.add(sectionToConfirm);
                return next;
            });
            setModifiedSections((prev) => {
                if (!prev.has(sectionToConfirm)) {
                    return prev;
                }
                const next = new Set(prev);
                next.delete(sectionToConfirm);
                return next;
            });
        }
        closeSection();
    };
    React.useEffect(() => {
        if (!showServiceSelection) {
            setActiveSection(null);
        }
    }, [showServiceSelection]);
    const isSectionNavDisabled = (targetSection) => targetSection !== "connectivity";
    const renderSectionButton = (text, iconName, section, styles) => (_jsxs("div", { style: { position: "relative" }, children: [_jsx(ActionButton, { text: text, iconProps: { iconName }, styles: styles, disabled: isSectionNavDisabled(section), onClick: () => openSection(section) }), confirmedSections.has(section) && (_jsx(Icon, { iconName: "ShoppingCartSolid", ariaLabel: t("Section complete"), style: {
                    position: "absolute",
                    right: 10,
                    bottom: 10,
                    color: "#0078d4",
                    fontSize: 14,
                    pointerEvents: "none",
                } }))] }));
    const getSectionTitle = () => {
        switch (activeSection) {
            case "connectivity":
                return t("Connectivity Services");
            case "tech-design":
                return t("Tech Design Services");
            case "deployment":
                return t("Deployment Services");
            case "rapid":
                return t("RAPID Deployment");
            case "noc":
                return t("NOC Services");
            case "security":
                return t("Security Services");
            case "maintenance":
                return t("Maintenance Services");
            case "lifecycle":
                return t("Lifecycle Refreshes");
            default:
                return t("Configure Section");
        }
    };
    const renderSectionModalContent = () => {
        switch (activeSection) {
            case "connectivity":
                return (_jsxs("div", { onChangeCapture: markActiveSectionAsModified, onInputCapture: markActiveSectionAsModified, children: [_jsxs(Stack, { tokens: { childrenGap: 12 }, styles: { root: { marginBottom: 12 } }, children: [_jsx(Dropdown, { label: t("Which Country is this site located?"), options: countryOptions, selectedKey: form.country, onChange: (e, option) => onChange(e, option?.key?.toString() ?? "", "country"), required: true, errorMessage: errors.country }), _jsx(Dropdown, { label: t("What type of site is this location?"), options: siteTypeOptions, selectedKey: form.site_type, onChange: (e, option) => onChange(e, option?.key?.toString() ?? "", "site_type"), required: true, errorMessage: errors.site_type }), _jsx(Dropdown, { label: t("What Term do you require this over?"), options: termOptions, selectedKey: form.term_required, onChange: (e, option) => onChange(e, option?.key?.toString() ?? "", "term_required"), required: true, errorMessage: errors.term_required }), _jsx(Dropdown, { label: t("Satellite Packages"), options: satellitePackagesOptions, selectedKey: form.satellite_packages, onChange: (e, option) => onChange(e, option?.key?.toString() ?? "", "satellite_packages"), errorMessage: errors.satellite_packages, required: true }), _jsx(CommunicationsServicesPicker, { form: form, errorMessage: errors.communications_services, onChange: (e, value, fieldName) => onChange(e, value, fieldName) })] }), _jsxs(Stack, { tokens: { childrenGap: 12 }, styles: { root: { marginBottom: 12 } }, children: [_jsx(Checkbox, { label: t("Do you need bonded 4G/5G or Satellite Services?"), checked: form.needs_bonded_services, onChange: (_, checked) => onChange(null, !!checked, "needs_bonded_services") }), _jsx(Dropdown, { label: t("Static public IPv4 addresses required per site"), options: staticPublicIpCountOptions, selectedKey: form.static_ip_count ?? 0, onChange: (e, option) => {
                                        const count = typeof option?.key === "number" ? option.key : 0;
                                        onChange(e, count, "static_ip_count");
                                    } })] }), _jsxs(Stack, { tokens: { childrenGap: 12 }, styles: { root: { marginBottom: 12 } }, children: [_jsx(Checkbox, { label: t("Is a Site Survey Required?"), checked: form.site_survey_required, onChange: (_, checked) => onChange(null, !!checked, "site_survey_required") }), _jsx(Checkbox, { label: t("Is Installation Lift Equipment Required?"), checked: form.installation_lift_equipment_required, onChange: (_, checked) => onChange(null, !!checked, "installation_lift_equipment_required") }), _jsx(Checkbox, { label: t("Project Management"), checked: form.project_management_required, onChange: (_, checked) => onChange(null, !!checked, "project_management_required") }), _jsx(Checkbox, { label: t("Health and Safety Admin"), checked: form.h_s_documentation_required, onChange: (_, checked) => onChange(null, !!checked, "h_s_documentation_required") }), _jsx(TextField, { label: t("If YES do you have the H&S Requirements?"), value: form.has_h_s_requirements || "", onChange: (e, value) => onChange(e, value ?? "", "has_h_s_requirements"), multiline: true, rows: 3 })] }), _jsx(Stack, { tokens: { childrenGap: 12 }, styles: { root: { marginBottom: 12 } }, children: _jsx(Checkbox, { label: t("Does the site need RAPID Deployment?"), checked: form.needs_rapid_deployment, onChange: (_, checked) => onChange(null, !!checked, "needs_rapid_deployment") }) }), _jsxs(Stack, { tokens: { childrenGap: 12 }, styles: { root: { marginBottom: 12 } }, children: [_jsx(Checkbox, { label: t("Is this a Temporary Solution?"), checked: form.is_temporary, onChange: (_, checked) => onChange(null, !!checked, "is_temporary") }), _jsx(Checkbox, { label: t("Do you need Priority Shipping for this location?"), checked: form.priority_shipping, onChange: (_, checked) => onChange(null, !!checked, "priority_shipping") })] }), _jsxs(Stack, { tokens: { childrenGap: 12 }, styles: { root: { marginBottom: 12 } }, children: [_jsx(Checkbox, { label: t("Is Out of Band Management Required?"), checked: form.out_of_band_management_required, onChange: (_, checked) => onChange(null, !!checked, "out_of_band_management_required") }), _jsx(Checkbox, { label: t("Managed service"), checked: form.managed_service_required, onChange: (_, checked) => onChange(null, !!checked, "managed_service_required") })] }), _jsx(Stack, { tokens: { childrenGap: 12 }, styles: { root: { marginBottom: 12 } }, children: _jsx(TextField, { label: t("Notes"), value: form.notes || "", onChange: (e, value) => onChange(e, value ?? "", "notes"), multiline: true, rows: 3 }) })] }));
            default:
                return _jsx("div", { children: t("Select a section") });
        }
    };
    return (_jsxs("form", { onSubmit: handleSubmit, style: { maxHeight: "70vh", overflowY: "auto", padding: 16 }, children: [_jsxs(Stack, { tokens: { childrenGap: 16 }, children: [showSiteDetails ? (_jsxs("div", { style: {
                            border: "1px solid #e1dfdd",
                            borderRadius: 12,
                            padding: 16,
                            background: "#fff",
                        }, children: [_jsx("h3", { style: { marginTop: 0, marginBottom: 8 }, children: t("Site Details") }), _jsx("p", { style: {
                                    margin: "0 0 16px 0",
                                    fontSize: 12,
                                    lineHeight: 1.45,
                                    color: "#605e5c",
                                }, children: t("Figures and dates in this section are estimates only and may change after technical design review and on-site validation.") }), _jsxs(Stack, { horizontal: true, tokens: { childrenGap: 12 }, children: [_jsx(TextField, { name: "customer_name", label: t("Customer Name"), value: form.customer_name || "", onChange: (e, value) => onChange(e, value ?? "", "customer_name"), required: true, errorMessage: errors.customer_name, styles: { root: { flex: 1 } } }), _jsx(TextField, { name: "site_name", label: t("Site Name"), value: form.site_name || "", onChange: (e, value) => onChange(e, value ?? "", "site_name"), styles: { root: { flex: 1 } }, required: true, errorMessage: errors.site_name }), _jsx(TextField, { name: "site_code", label: t("Site Code"), value: form.site_code, onChange: (e, value) => onChange(e, value ?? "", "site_code"), required: true, errorMessage: errors.site_code, styles: { root: { flex: 1 } } }), _jsx(TextField, { name: "entity", label: t("Tax Entity"), value: form.entity || "", onChange: (e, value) => onChange(e, value ?? "", "entity"), required: true, errorMessage: errors.entity, styles: { root: { flex: 1 } } })] }), _jsxs(Stack, { horizontal: true, tokens: { childrenGap: 12 }, children: [_jsx(TextField, { name: "contact_email", label: t("Contact Email"), type: "email", value: form.contact_email || "", onChange: (e, value) => onChange(e, value ?? "", "contact_email"), required: true, errorMessage: errors.contact_email, styles: { root: { flex: 1 } } }), _jsx(TextField, { name: "reference_id", label: t("Reference ID"), value: form.reference_id, onChange: (e, value) => onChange(e, value ?? "", "reference_id"), required: true, errorMessage: errors.reference_id, styles: { root: { flex: 1 } } }), _jsxs("div", { style: { flex: 1 }, children: [_jsx(DatePicker, { label: t("Go Live Date"), value: form.go_live_date ? new Date(form.go_live_date) : new Date(), onSelectDate: (date) => onChange(null, date ?? null, "go_live_date"), isRequired: true }), errors.go_live_date && (_jsx("span", { style: { color: "#a4262c", fontSize: 12 }, children: errors.go_live_date }))] }), _jsx(TextField, { label: t("Number of Sites for Deployment"), type: "number", name: "number_of_sites", value: form.number_of_sites?.toString() || "1", onChange: (e, value) => onChange(e, value ?? "1", "number_of_sites"), required: true, styles: { root: { flex: 1, minWidth: 160 } }, errorMessage: errors.number_of_sites })] }), _jsx(TextField, { name: "address", label: t("Address"), value: form.address, onChange: (e, value) => onChange(e, value ?? "", "address"), required: true, errorMessage: errors.address }), _jsxs(Stack, { horizontal: true, tokens: { childrenGap: 12 }, children: [_jsx(TextField, { name: "project_manager", label: t("Project Manager"), value: form.project_manager || "", onChange: (e, value) => onChange(e, value ?? "", "project_manager"), styles: { root: { flex: 1 } } }), _jsx(TextField, { name: "contact_name", label: t("Contact Name"), value: form.contact_name || "", onChange: (e, value) => onChange(e, value ?? "", "contact_name"), styles: { root: { flex: 1 } } }), _jsx(TextField, { name: "contact_number", label: t("Contact Number"), type: "tel", value: form.contact_number || "", onChange: (e, value) => onChange(e, value ?? "", "contact_number"), errorMessage: errors.contact_number, styles: { root: { flex: 1 } } })] })] })) : null, showServiceSelection ? (_jsx("div", { style: {
                            border: "1px solid #e1dfdd",
                            borderRadius: 12,
                            padding: 16,
                            background: "#f8f8f8",
                        }, children: _jsxs(Stack, { horizontal: true, tokens: { childrenGap: 12 }, children: [_jsxs("div", { style: { width: "24%" }, children: [renderSectionButton(t('"GLU Connect" Connectivity Services'), "Globe", "connectivity", sideButtonStyle(getBooleanActive("needs_bonded_services"))), renderSectionButton(t("Tech Design Services"), "Design", "tech-design", sideButtonStyle(getBooleanActive("site_survey_required"))), renderSectionButton(t("Deployment Services"), "Build", "deployment", sideButtonStyle(getBooleanActive("needs_rapid_deployment"))), renderSectionButton(t("RAPID Deployment"), "Rocket", "rapid", sideButtonStyle(getBooleanActive("is_temporary")))] }), _jsxs("div", { style: {
                                        width: "52%",
                                        border: "1px solid #e1dfdd",
                                        borderRadius: 16,
                                        padding: 16,
                                        background: "#ffffff",
                                    }, children: [_jsx("div", { style: { fontWeight: 700, marginBottom: 8, color: "#605e5c" }, children: t("Workplace Services") }), _jsxs("div", { style: centerTileGridStyle, children: [_jsx(ActionButton, { text: t("Unified Comms"), styles: centerTileStyle(getNumberActive("standard_cpe")), iconProps: { iconName: "CellPhone" }, disabled: true, onClick: () => openSection("connectivity") }), _jsx(ActionButton, { text: t("Digital Workplace"), styles: centerTileStyle(getNumberActive("standard_cpe")), iconProps: { iconName: "Work" }, disabled: true, onClick: () => openSection("lifecycle") }), _jsx(ActionButton, { text: t("Digital Signage"), styles: centerTileStyle(getBooleanActive("out_of_band_management_required")), iconProps: { iconName: "TVMonitor" }, disabled: true, onClick: () => openSection("security") }), _jsx(ActionButton, { text: t("ePOS"), styles: centerTileStyle(getNumberActive("advanced_cpe")), iconProps: { iconName: "Shop" }, disabled: true, onClick: () => openSection("connectivity") }), _jsx(ActionButton, { text: t("IoT"), styles: centerTileStyle(getBooleanActive("installation_lift_equipment_required")), iconProps: { iconName: "InternetSharing" }, disabled: true, onClick: () => openSection("security") }), _jsx(ActionButton, { text: t("OT"), styles: centerTileStyle(getBooleanActive("h_s_documentation_required")), iconProps: { iconName: "Settings" }, disabled: true, onClick: () => openSection("lifecycle") })] }), _jsx("div", { style: {
                                                fontWeight: 700,
                                                marginTop: 12,
                                                marginBottom: 8,
                                                color: "#605e5c",
                                            }, children: t("Network Services") }), _jsxs("div", { style: centerTileGridStyle, children: [_jsx(ActionButton, { text: t("Public / Private 5G"), styles: centerTileStyle(getBooleanActive("needs_bonded_services")), iconProps: { iconName: "CellPhone" }, disabled: true, onClick: () => openSection("connectivity") }), _jsx(ActionButton, { text: t("Satellite"), styles: centerTileStyle(hasCommunicationsServiceSelection(form)), iconProps: { iconName: "MapPin" }, disabled: true, onClick: () => openSection("connectivity") }), _jsx(ActionButton, { text: t("Wireless"), styles: centerTileStyle(false), iconProps: { iconName: "WifiWarning4" }, disabled: true, onClick: () => openSection("connectivity") }), _jsx(ActionButton, { text: t("LAN Services"), styles: centerTileStyle(getNumberActive("advanced_cpe")), iconProps: { iconName: "BranchMerge" }, disabled: true, onClick: () => openSection("lifecycle") }), _jsx(ActionButton, { text: t("Firewall Services"), styles: centerTileStyle(getBooleanActive("out_of_band_management_required")), iconProps: { iconName: "Shield" }, disabled: true, onClick: () => openSection("security") }), _jsx(ActionButton, { text: t("Connectivity"), styles: centerTileStyle(hasCommunicationsServiceSelection(form)), iconProps: { iconName: "Globe" }, disabled: true, onClick: () => openSection("connectivity") })] }), _jsx("div", { style: {
                                                fontWeight: 700,
                                                marginTop: 12,
                                                marginBottom: 8,
                                                color: "#605e5c",
                                            }, children: t("Physical Security") }), _jsxs("div", { style: centerTileGridStyle, children: [_jsx(ActionButton, { text: t("Drones"), styles: centerTileStyle(getBooleanActive("out_of_band_management_required")), iconProps: { iconName: "Airplane" }, disabled: true, onClick: () => openSection("noc") }), _jsx(ActionButton, { text: t("Access Control"), styles: centerTileStyle(getBooleanActive("installation_lift_equipment_required")), iconProps: { iconName: "Permissions" }, disabled: true, onClick: () => openSection("security") }), _jsx(ActionButton, { text: t("Alarm System"), styles: centerTileStyle(getBooleanActive("priority_shipping")), iconProps: { iconName: "Ringer" }, disabled: true, onClick: () => openSection("security") }), _jsx(ActionButton, { text: t("Air Quality / Sensors"), styles: centerTileStyle(getBooleanActive("installation_lift_equipment_required")), iconProps: { iconName: "Processing" }, disabled: true, onClick: () => openSection("security") }), _jsx(ActionButton, { text: t("Control System"), styles: centerTileStyle(getNumberActive("static_ip_count")), iconProps: { iconName: "Settings" }, disabled: true, onClick: () => openSection("tech-design") }), _jsx(ActionButton, { text: t("CCTV"), styles: centerTileStyle(getBooleanActive("site_survey_required")), iconProps: { iconName: "Video" }, disabled: true, onClick: () => openSection("security") })] })] }), _jsxs("div", { style: { width: "24%" }, children: [renderSectionButton(t("NOC Services"), "Settings", "noc", sideButtonStyle(getBooleanActive("out_of_band_management_required"))), renderSectionButton(t("Security Services"), "Shield", "security", sideButtonStyle(getBooleanActive("h_s_documentation_required"))), renderSectionButton(t("Maintenance Services"), "Repair", "maintenance", sideButtonStyle(getBooleanActive("site_survey_required"))), renderSectionButton(t("Lifecycle Refreshes"), "Sync", "lifecycle", sideButtonStyle(getBooleanActive("priority_shipping")))] })] }) })) : null] }), _jsxs(Dialog, { hidden: !activeSection || !showServiceSelection, onDismiss: closeSection, dialogContentProps: {
                    type: DialogType.largeHeader,
                    title: getSectionTitle(),
                }, maxWidth: 1440, children: [renderSectionModalContent(), _jsxs(DialogFooter, { children: [_jsx(PrimaryButton, { onClick: handleSectionDoneClick, text: t("Done") }), _jsx(DefaultButton, { onClick: closeSection, text: t("Close") })] })] })] }));
};
export default SiteInABoxForm;
